import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, map, of, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Un punto de la curva de peso. */
export interface RegistroPeso {
  fecha : string;   // ISO
  kg : number;
}

/** Una vacuna o control que viene, tal como lo arma la veterinaria. */
export interface Recordatorio {
  descripcion : string;
  fecha : string;   // ISO
}

/**
 * Una visita a la veterinaria, ya filtrada a lo que se le muestra al cliente.
 * Se dejan afuera a proposito todos los campos de diagnostico interno del
 * VisitDto (initialDiagnosis, treatment, prescription, internalDescription,
 * examenes, etc.): son notas del profesional, no algo para exhibir en Mi Cuenta.
 */
export interface VisitaClinica {
  fecha : string;      // ISO
  motivo : string;     // visitCategory.name: "General", "Vacunacion", ...
  pesoKg? : number;
  lugar? : string;
  practicas : string[]; // nombres de las practicas aplicadas (vacunas, etc.)
}

export interface HistoriaClinica {
  peso : RegistroPeso[];
  visitas : VisitaClinica[];
  recordatorios : Recordatorio[];
  /** true si las tres fuentes vinieron vacias: la mascota no tiene historia. */
  vacia : boolean;
}

/**
 * Historia clinica de una mascota: peso, visitas y proximos controles.
 *
 * Va contra la API de Vet (vetServiceUrl), la misma que ya usa la app para
 * razas y especies. Es una API distinta de singleCustomerUrl, asi que el
 * AuthenticationInterceptor no la toca; ademas estos endpoints hoy responden
 * sin credenciales (ver nota abajo), pero se mandan igual por consistencia con
 * el resto de las llamadas a servicios de Puppis.
 *
 * NOTA DE SEGURIDAD (para reportar a la consultora): estos endpoints devuelven
 * la historia clinica completa de cualquier mascota con solo pasar el petId,
 * sin autenticacion. Iterando petId se lee toda la base. No es algo que se
 * arregle desde el front.
 */
@Injectable()
export class PetClinicalService {

  constructor(private http : HttpClient) {}

  private get headers() : HttpHeaders {
    return new HttpHeaders({
      'X-Api-Key': environment.apikey,
      'X-Api-Secret': environment.apisecret
    });
  }

  /** El backend de Vet devuelve el envoltorio de ABP: {result, success, ...}. */
  private soloResult<T>(respuesta : any) : T {
    return (respuesta?.result ?? respuesta) as T;
  }

  obtenerHistoria(petId : number) : Observable<HistoriaClinica> {

    const base = environment.vetServiceUrl + '/api/services/app';
    const h = { headers: this.headers };

    // WeightReport y PetHistoryReport son POST y exigen Content-Length: mandar
    // {} evita el "HTTP 411 Length Required" que da un POST sin cuerpo.
    const peso$ = this.http
      .post<any>(`${base}/PetReport/WeightReport?petId=${petId}`, {}, h)
      .pipe(map(r => this.mapearPeso(this.soloResult<any[]>(r))),
            catchError(() => of([] as RegistroPeso[])));

    const historia$ = this.http
      .post<any>(`${base}/PetReport/PetHistoryReport?petId=${petId}`, {}, h)
      .pipe(map(r => this.mapearVisitas(this.soloResult<any>(r))),
            catchError(() => of([] as VisitaClinica[])));

    const recordatorios$ = this.http
      .get<any>(`${base}/Reminder/GetAll?PetId=${petId}`, h)
      .pipe(map(r => this.mapearRecordatorios(this.soloResult<any[]>(r))),
            catchError(() => of([] as Recordatorio[])));

    return forkJoin({ peso: peso$, visitas: historia$, recordatorios: recordatorios$ })
      .pipe(map(({ peso, visitas, recordatorios }) => ({
        peso,
        visitas,
        recordatorios,
        vacia: peso.length === 0 && visitas.length === 0 && recordatorios.length === 0
      })));
  }

  /**
   * Proximo recordatorio (vacuna/control) de una mascota, para el tag de la
   * tarjeta. Una sola llamada liviana a Reminder/GetAll. Devuelve el futuro mas
   * cercano; si no hay futuros, el mas reciente; null si no hay ninguno.
   */
  proximaVacuna(petId : number) : Observable<Recordatorio | null> {

    const url = `${environment.vetServiceUrl}/api/services/app/Reminder/GetAll?PetId=${petId}`;

    return this.http.get<any>(url, { headers: this.headers }).pipe(
      map(r => {
        const rs = this.mapearRecordatorios(this.soloResult<any[]>(r));
        if (!rs.length) return null;
        const hoy = new Date().toISOString().slice(0, 10);
        const futuros = rs.filter(x => x.fecha >= hoy);
        return (futuros[0] ?? rs[rs.length - 1]) ?? null;
      }),
      catchError(() => of(null))
    );
  }

  private mapearPeso(datos : any[]) : RegistroPeso[] {
    return (datos ?? [])
      .filter(p => p && p.date && p.weight != null && p.weight > 0)
      .map(p => ({ fecha: p.date, kg: p.weight }))
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }

  private mapearRecordatorios(datos : any[]) : Recordatorio[] {
    return (datos ?? [])
      .filter(r => r && r.description && r.date)
      .map(r => ({ descripcion: String(r.description).trim(), fecha: r.date }))
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }

  private mapearVisitas(historia : any) : VisitaClinica[] {

    const visitas : any[] = historia?.visits ?? [];

    return visitas
      .filter(v => v && v.visitDate)
      .map(v => ({
        fecha : v.visitDate,
        motivo : v.visitCategory?.name?.trim() || 'Consulta',
        pesoKg : (v.weight != null && v.weight > 0) ? v.weight : undefined,
        lugar : (v.store?.name && v.store.name !== 'Desconocido') ? v.store.name : undefined,
        practicas : (v.visitPractices ?? [])
          .map((p : any) => p?.practice?.name?.trim())
          .filter((n : any) => !!n)
      }))
      .sort((a, b) => b.fecha.localeCompare(a.fecha)); // mas reciente primero
  }
}
